import type { PvpConfig, PvpSettings, UpdatePvpConfigBody } from '@/types/pvp';
import { apiFieldErrors } from '@/utils/apiError';

/** The values the settings form edits, flat (grouped ones go under review, bot or matchmaking on the wire). */
export interface PvpSettingsForm {
  entryFee: number;
  dailyPaidMatches: number;
  minBalls: number;
  runsPerBall: number;
  sixRate: number;
  botAfterSec: number;
  botRatingOffset: number;
  startWidth: number;
  widerAfterSec: number;
  widerWidth: number;
  anyoneAfterSec: number;
}
export type PvpField = keyof PvpSettingsForm;

type Group = 'review' | 'bot' | 'matchmaking';

/** Where a grouped field lives on the wire: its group and its key there. */
const WIRE: Partial<Record<PvpField, [Group, string]>> = {
  minBalls: ['review', 'minBalls'],
  runsPerBall: ['review', 'runsPerBall'],
  sixRate: ['review', 'sixRate'],
  botAfterSec: ['bot', 'afterSec'],
  botRatingOffset: ['bot', 'ratingOffset'],
  startWidth: ['matchmaking', 'startWidth'],
  widerAfterSec: ['matchmaking', 'widerAfterSec'],
  widerWidth: ['matchmaking', 'widerWidth'],
  anyoneAfterSec: ['matchmaking', 'anyoneAfterSec'],
};

export const PVP_FIELDS: PvpField[] = [
  'entryFee',
  'dailyPaidMatches',
  'minBalls',
  'runsPerBall',
  'sixRate',
  'botAfterSec',
  'botRatingOffset',
  'startWidth',
  'widerAfterSec',
  'widerWidth',
  'anyoneAfterSec',
];

/**
 * Ranges the backend accepts (PVP_SETTINGS_LIMITS in wcc4-backend
 * pvp-settings.ts; a DTO failure answers 422). `aboveMin`: the minimum itself
 * is refused (the rates must be above 0).
 */
export const PVP_RANGE: Record<
  PvpField,
  { min: number; max: number; whole: boolean; aboveMin?: boolean }
> = {
  entryFee: { min: 0, max: 1000, whole: true },
  dailyPaidMatches: { min: 0, max: 100, whole: true },
  minBalls: { min: 1, max: 1000, whole: true },
  runsPerBall: { min: 0, max: 6, whole: false, aboveMin: true },
  sixRate: { min: 0, max: 1, whole: false, aboveMin: true },
  botAfterSec: { min: 5, max: 120, whole: true },
  botRatingOffset: { min: -500, max: 500, whole: true },
  startWidth: { min: 25, max: 2000, whole: true },
  widerAfterSec: { min: 1, max: 120, whole: true },
  widerWidth: { min: 25, max: 2000, whole: true },
  anyoneAfterSec: { min: 2, max: 300, whole: true },
};

/** The review rule when the admin row stores none (PVP_REVIEW_DEFAULTS in pvp-settings.ts). */
export const PVP_REVIEW_DEFAULTS = {
  minBalls: 30,
  runsPerBall: 3.5,
  sixRate: 0.45,
};

/**
 * A grouped field's value when the admin row stores none: the review rule, and
 * PVP_BOT_DEFAULTS / PVP_MATCHMAKING_DEFAULTS in pvp-settings.ts (today's queue).
 */
export const PVP_DEFAULTS: Partial<Record<PvpField, number>> = {
  ...PVP_REVIEW_DEFAULTS,
  botAfterSec: 20,
  botRatingOffset: 0,
  startWidth: 100,
  widerAfterSec: 8,
  widerWidth: 300,
  anyoneAfterSec: 15,
};

export const PVP_FIELD_LABEL: Record<PvpField, string> = {
  entryFee: 'Entry fee',
  dailyPaidMatches: 'Paid matches a day',
  minBalls: 'Minimum balls',
  runsPerBall: 'Runs a ball',
  sixRate: 'Sixes a ball',
  botAfterSec: 'Bot offered after',
  botRatingOffset: 'Bot strength',
  startWidth: 'Starting range',
  widerAfterSec: 'Widen after',
  widerWidth: 'Wider range',
  anyoneAfterSec: 'Anyone after',
};

/** The env variable a fee or cap falls back to. */
export const PVP_ENV_NAME: Partial<Record<PvpField, string>> = {
  entryFee: 'PVP_ENTRY_FEE',
  dailyPaidMatches: 'PVP_DAILY_PAID_MATCHES',
};

export function pvpFormValues(settings: PvpSettings): PvpSettingsForm {
  return {
    entryFee: settings.entryFee,
    dailyPaidMatches: settings.dailyPaidMatches,
    minBalls: settings.review.minBalls,
    runsPerBall: settings.review.runsPerBall,
    sixRate: settings.review.sixRate,
    botAfterSec: settings.bot.afterSec,
    botRatingOffset: settings.bot.ratingOffset,
    startWidth: settings.matchmaking.startWidth,
    widerAfterSec: settings.matchmaking.widerAfterSec,
    widerWidth: settings.matchmaking.widerWidth,
    anyoneAfterSec: settings.matchmaking.anyoneAfterSec,
  };
}

/** Why the backend would refuse `value` for `field`, or null when it takes it. */
export function pvpFieldError(field: PvpField, value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'Enter a number';
  const { min, max, whole, aboveMin } = PVP_RANGE[field];
  if (whole) {
    return Number.isInteger(value) && value >= min && value <= max
      ? null
      : `A whole number from ${min} to ${max}`;
  }
  const aboveFloor = aboveMin ? value > min : value >= min;
  return aboveFloor && value <= max ? null : `Above ${min}, up to ${max}`;
}

type Matchmaking = Pick<
  PvpSettingsForm,
  'startWidth' | 'widerAfterSec' | 'widerWidth' | 'anyoneAfterSec'
>;

/**
 * The rule across the matchmaking group (matchmakingRule in pvp-settings.ts):
 * the range never narrows as a player waits, and anyone comes after widening.
 * The field to show it on, or null when the group keeps it.
 */
export function pvpMatchmakingError(m: Matchmaking): { field: PvpField; message: string } | null {
  if (m.startWidth > m.widerWidth) {
    return {
      field: 'startWidth',
      message: `The starting range must not be wider than the wider range (±${m.widerWidth})`,
    };
  }
  if (m.widerAfterSec >= m.anyoneAfterSec) {
    return {
      field: 'anyoneAfterSec',
      message: `Must be later than Widen after (${m.widerAfterSec} s)`,
    };
  }
  return null;
}

export type PvpSource = 'stored' | 'env' | 'default';

const storedValue = (config: PvpConfig, field: PvpField): unknown => {
  const wire = WIRE[field];
  if (!wire) return config.stored?.[field as 'entryFee' | 'dailyPaidMatches'];
  const group = config.stored?.[wire[0]] as Record<string, unknown> | undefined;
  return group?.[wire[1]];
};

/** The stored matchmaking group as the servers read it: valid fields, the rest at default. */
const storedMatchmaking = (config: PvpConfig): Matchmaking => {
  const m = {} as Matchmaking;
  for (const field of ['startWidth', 'widerAfterSec', 'widerWidth', 'anyoneAfterSec'] as const) {
    const v = storedValue(config, field);
    m[field] = typeof v === 'number' && pvpFieldError(field, v) === null ? v : PVP_DEFAULTS[field]!;
  }
  return m;
};

/**
 * Where a value in force comes from, and what clearing it falls back to. A
 * stored value counts only when it is in range, as in the backend's
 * resolvePvpSettings; anything else there is ignored by the servers too. A
 * stored matchmaking group that breaks the group rule is ignored as a whole.
 */
export function pvpFieldSource(
  config: PvpConfig,
  field: PvpField
): { source: PvpSource; fallback: number } {
  const stored = storedValue(config, field);
  const valid = pvpFieldError(field, stored as number | undefined) === null;
  const wire = WIRE[field];
  if (wire) {
    const brokenGroup =
      wire[0] === 'matchmaking' && pvpMatchmakingError(storedMatchmaking(config)) !== null;
    return {
      source: valid && !brokenGroup ? 'stored' : 'default',
      fallback: PVP_DEFAULTS[field]!,
    };
  }
  const envField = field as 'entryFee' | 'dailyPaidMatches';
  return { source: valid ? 'stored' : 'env', fallback: config.env[envField] };
}

/** Puts one field's value (or null to clear it) where it goes in a PUT body. */
function setWire(body: UpdatePvpConfigBody, field: PvpField, value: number | null): void {
  const wire = WIRE[field];
  if (!wire) {
    body[field as 'entryFee' | 'dailyPaidMatches'] = value;
    return;
  }
  const [group, key] = wire;
  const into = (body[group] ?? {}) as Record<string, number | null>;
  into[key] = value;
  (body as Record<string, unknown>)[group] = into;
}

/** One field's value in a PUT body, undefined when the body leaves it alone. */
function getWire(body: UpdatePvpConfigBody, field: PvpField): number | null | undefined {
  const wire = WIRE[field];
  if (!wire) return body[field as 'entryFee' | 'dailyPaidMatches'];
  return (body[wire[0]] as Record<string, number | null> | undefined)?.[wire[1]];
}

/**
 * The PUT body: only the fields that differ from what was loaded. The backend
 * patches field by field, so sending an untouched value would pin it (a fee
 * taken from env would stop following env) or undo another admin's change.
 */
export function pvpConfigChanges(
  loaded: PvpSettingsForm,
  values: PvpSettingsForm
): UpdatePvpConfigBody {
  const body: UpdatePvpConfigBody = {};
  for (const field of PVP_FIELDS) {
    if (values[field] !== loaded[field]) setWire(body, field, values[field]);
  }
  return body;
}

/** The PUT body that clears one field back to env (fee, cap) or its default. */
export function pvpClearBody(field: PvpField): UpdatePvpConfigBody {
  const body: UpdatePvpConfigBody = {};
  setWire(body, field, null);
  return body;
}

/** The PUT body for the pause switch: resuming clears the field (not paused is the default). */
export function pvpPauseBody(paused: boolean): UpdatePvpConfigBody {
  return { paused: paused ? true : null };
}

/** The confirm shown before the pause switch saves. */
export function pvpPauseConfirm(pause: boolean): {
  title: string;
  okText: string;
  lines: string[];
} {
  const pickup = 'Every PvP server picks this up within about 30 s.';
  return pause
    ? {
        title: 'Pause new PvP matches?',
        okText: 'Pause',
        lines: [
          'Players in the queue are taken out and told the servers are busy; joining and asking for a bot answer busy too.',
          'Matches already being played carry on and settle as usual.',
          pickup,
        ],
      }
    : {
        title: 'Resume PvP matches?',
        okText: 'Resume',
        lines: ['Players can join the queue and ask for a bot again.', pickup],
      };
}

/** A value with its unit, and what 0 means for the fee, the cap and the bot strength. */
export function pvpValueText(field: PvpField, value: number): string {
  switch (field) {
    case 'entryFee':
      return value === 0 ? '0 coins (free, rating only)' : `${value} coins`;
    case 'dailyPaidMatches':
      return value === 0 ? 'no cap' : `${value} a day`;
    case 'minBalls':
      return `${value} balls`;
    case 'botAfterSec':
    case 'widerAfterSec':
    case 'anyoneAfterSec':
      return `${value} s`;
    case 'botRatingOffset':
      if (value === 0) return "the player's own team rating";
      return value > 0 ? `+${value} team rating (stronger)` : `${value} team rating (weaker)`;
    case 'startWidth':
    case 'widerWidth':
      return `±${value} rating`;
    default:
      return String(value);
  }
}

/** One line per field in `body`, old value to new, for the save confirm. */
export function pvpChangeLines(loaded: PvpSettingsForm, body: UpdatePvpConfigBody): string[] {
  const lines: string[] = [];
  for (const field of PVP_FIELDS) {
    const next = getWire(body, field);
    if (typeof next !== 'number') continue;
    lines.push(
      `${PVP_FIELD_LABEL[field]}: ${pvpValueText(field, loaded[field])} → ${pvpValueText(field, next)}`
    );
  }
  return lines;
}

/** A 422's field reasons as form errors (`review.sixRate` goes under `sixRate`, `bot.afterSec` under `botAfterSec`). */
export function pvpFormFieldErrors(error: unknown): Array<{ name: PvpField; errors: string[] }> {
  const byPath = new Map<string, PvpField>(
    PVP_FIELDS.map((field) => {
      const wire = WIRE[field];
      return [wire ? `${wire[0]}.${wire[1]}` : field, field];
    })
  );
  const out: Array<{ name: PvpField; errors: string[] }> = [];
  for (const [path, reason] of Object.entries(apiFieldErrors(error))) {
    const name = byPath.get(path);
    if (name) out.push({ name, errors: [reason] });
  }
  return out;
}
